import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    Input,
    OnInit,
    ViewEncapsulation,
    input,
    inject,
    computed,
    signal,
} from '@angular/core';
import {
    ApplicationSettingsType,
    AccessRightType,
    FiImageSize,
    FileType,
    ObjectTypes,
    SideListOptionType,
} from '@fi-shared/types';
import { FiMediaBankService, IMediaBankFile } from '@fi-shared/media-bank';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { MediaBankHelperService } from '@fi-shared/media-bank/media-bank-helper.service';
import { ILightWeightObject } from '@fi-shared/models';
import { FiNotificationHelperService } from '@fi-shared/fi-notification/fi-notification-helper.service';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { IPreviewImageCellValue } from '@fi-shared/fi-wrapper-table/fi-wrapper-table';
import { FiDisplayType } from '@fi-shared/types/display-type';
import { IItemsListAction2, ISideListOptionsItem } from '@fi-shared/fi-items-list/fi-items-list';
import { FiTagsListPositionTypes } from '@fi-app/components/items-list/enums';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { getSideListOptionsBottomFn } from '@fi-shared/fi-items-list/side-list-options';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { takeUntil } from 'rxjs/operators';
import { MediaBankService } from '../../media-bank.service';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { MediaBankFileModel } from '../../media-bank-file.model';
import { getObjectTypeNameTextId } from '@fi-app/shared/utils/object-type-name';
import { isDefined } from '@fi-shared/utils/is-defined';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';

@Component({
    selector: 'fi-media-bank-new-list',
    templateUrl: './media-bank-new-list.component.html',
    styleUrls: ['./media-bank-new-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-media-bank-new-list' },
    standalone: false,
})
export class MediaBankNewListComponent extends FiRemoteStateFullDataSource<IMediaBankFile> implements OnInit {
    public displayType = input<FiDisplayType>(FiDisplayType.List);
    public tagsPositionType = input<FiTagsListPositionTypes>();
    public limitedTagIds = input<number[]>([]);
    public preselectedTagId = input<number | undefined>(undefined);
    public isTagsSectionShown = input<boolean>(true);
    public isFavoriteIconShown = input<boolean>(true);
    public hasCheckedColumn = input<boolean>(false);

    @Input() allowMultiSelect = true;
    @Input() isItemActionsShown: boolean;
    @Input() isSideListShown: boolean = true;

    private mediaBankService = inject(MediaBankService);
    private fiMediaBankService = inject(FiMediaBankService);
    private mediaBankHelperService = inject(MediaBankHelperService);
    private notificationHelper = inject(FiNotificationHelperService);
    private mediaStorageService = inject(FiMediaStorageService);
    private detectDevice = inject(DetectDevicesService);
    private accessToApplicationTypeService = inject(AccessToApplicationTypeService);
    private fiItemsDeleteHelperService = inject(FiItemsDeleteHelperService);
    private fiUserDataService = inject(FiUserDataService);

    public defaultSortColumnName = 'PublicationStartDate';
    public itemsListType = FiItemsListType.MediaBank;
    public objectType = ObjectTypes.MediaBank;
    public defaultSortDirection = true;

    protected optionsTop: ISideListOptionsItem[] = [
        { value: SideListOptionType.SharedWithMe, text: this.appTextService.get('common.shared_with_me') },
        { value: SideListOptionType.New, text: this.appTextService.get('common.new_files') },
        { value: SideListOptionType.OnlyNotVisited, text: this.appTextService.get('mediabank.unread_files') },
        { value: SideListOptionType.CreatedBy, text: this.appTextService.get('common.created_by_me') },
        { value: SideListOptionType.Favorite, text: this.appTextService.get('common.my_favorites') },
        { value: SideListOptionType.Deleted, text: this.appTextService.get('common.trash') },
    ];
    protected readonly optionsBottom: ISideListOptionsItem[] = getSideListOptionsBottomFn(this.appTextService);
    protected itemsListActions = signal<IItemsListAction2[]>([]);

    protected isListLayout = computed(() => this.displayType() === FiDisplayType.List);
    protected showTagsTopSection = computed(
        () => this.tagsPositionType() === FiTagsListPositionTypes.Top && this.isTagsSectionShown(),
    );
    protected showTagsSidebar = computed(
        () =>
            (this.detectDevice.isMobilePhone() || this.tagsPositionType() === FiTagsListPositionTypes.Left) &&
            this.isTagsSectionShown(),
    );
    protected optionsTags = signal<ISideListOptionsItem[]>([]);

    private readonly hasSuperAdminRight = this.accessToApplicationTypeService.HasAccessToApplicationType(
        ApplicationSettingsType.MediaBankGlobal,
        AccessRightType.SuperAdmin,
    );

    private readonly restoreListAction: Readonly<IItemsListAction2> = {
        title: this.appTextService.get('common.restore'),
        onClick: () => this.restoreSelectedItems(),
    };

    private readonly moveToTrashListAction: Readonly<IItemsListAction2> = {
        title: this.appTextService.get('common.delete'),
        onClick: () => this.confirmMoveToTrashItems(),
    };

    private readonly deletePermanentlyListAction: Readonly<IItemsListAction2> = {
        title: this.appTextService.get('common.delete_permanently'),
        onClick: () => this.confirmDeletePermanentlySelectedItems(),
    };

    protected readonly isBrandProfileShown = this.fiUserDataService.getUserBusinessUnits().length > 1;

    constructor(injector: Injector) {
        super(injector);
    }

    public ngOnInit(): void {
        if (isDefined(this.preselectedTagId())) {
            this.selectedTagId = this.preselectedTagId();
        }
        this.setTags();
        this.initSideList();
        this.setItemsListActions();
        this.subscribeOnListReload();
    }

    private setTags(): void {
        this.mediaBankService.GetMediaBankFileTags().then((tags) => {
            if (!!this.limitedTagIds()?.length) {
                tags = tags.filter((tag) => this.limitedTagIds().includes(tag.Id));
            }
            this.optionsTags.set(
                tags.map((tag) => ({
                    value: tag.Id,
                    text: tag.Name,
                    Level: tag.Level,
                    ParentId: tag.ParentId,
                    IsDisabled: tag.IsDisabled,
                })),
            );
        });
    }

    private subscribeOnListReload() {
        this.mediaBankService.reloadMediaBankList$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.reloadData();
        });
    }

    private initSideList() {
        if (this.hasSuperAdminRight) {
            this.optionsTop = [
                {
                    value: SideListOptionType.All,
                    text: this.appTextService.get('common.all'),
                },
                ...this.optionsTop,
            ];
        }
    }

    public onSideListOptionsChanged(option: SideListOptionType) {
        super.onSideListOptionsChanged(option);
        this.setItemsListActions();
        this.clearSelectedItems();
    }

    private setItemsListActions() {
        if (this.filterType === SideListOptionType.Deleted) {
            this.itemsListActions.set([this.restoreListAction, this.deletePermanentlyListAction]);
        } else {
            this.itemsListActions.set([this.moveToTrashListAction]);
        }
    }

    private confirmMoveToTrashItems(): void {
        this.fiItemsDeleteHelperService
            .mediaBankDeleteConfirm(this.selectedItems() as MediaBankFileModel[])
            .then(() => this.moveToTrashSelectedItems());
    }

    private moveToTrashSelectedItems(): void {
        const selectedItems = this.selectedItems();
        this.mediaBankService.DeleteMediaBankFiles(selectedItems.map((item) => item.Id)).then(() => {
            this.removeData(selectedItems);
            this.clearSelectedItems();
            this.showItemsMovedToTrashSnackbar(selectedItems.length);
        });
    }

    private showItemsMovedToTrashSnackbar(itemsCount: number): void {
        const snackbarTextMessage =
            itemsCount +
            ' ' +
            this.appTextService.get(getObjectTypeNameTextId(this.objectType, itemsCount !== 1)) +
            ' ' +
            this.appTextService.get('history.moved_to_trash').toLowerCase();

        this.snackBar.showSnackbar({
            textMessage: snackbarTextMessage,
            buttonText: this.appTextService.get('common.dismiss'),
        });
    }

    private restoreSelectedItems(): void {
        this.fiItemsDeleteHelperService.restoreItems({
            itemsToRestore: this.selectedItems(),
            objectType: this.objectType,
            restoreFn: () => this.mediaBankService.RestoreMediaBankFiles(this.selectedItems().map((item) => item.Id)),
            afterRestoreFn: (restoredItems) => {
                this.removeData(restoredItems);
                this.showItemsRestoredSnackbar(restoredItems.length);
                this.clearSelectedItems();
            },
        });
    }

    private showItemsRestoredSnackbar(itemsCount: number): void {
        const snackbarTextMessage =
            itemsCount +
            ' ' +
            this.appTextService.get(getObjectTypeNameTextId(this.objectType, itemsCount !== 1)) +
            ' ' +
            this.appTextService.get('history.restored').toLowerCase();

        this.snackBar.showSnackbar({
            textMessage: snackbarTextMessage,
            buttonText: this.appTextService.get('common.dismiss'),
        });
    }

    private confirmDeletePermanentlySelectedItems(): void {
        this.fiItemsDeleteHelperService
            .mediaBankDeleteConfirm(
                this.selectedItems() as MediaBankFileModel[],
                this.appTextService.get('mediabank.delete_permanently_confirm'),
            )
            .then(() => this.deletePermanentlySelectedItems());
    }

    private deletePermanentlySelectedItems(): void {
        const selectedItems = this.selectedItems();
        this.mediaBankService.DeleteMediaBankFiles(selectedItems.map((item) => item.Id)).then(() => {
            this.removeData(selectedItems);
            this.clearSelectedItems();
            this.showItemsDeletedPermanentlySnackbar(selectedItems.length);
        });
    }

    private showItemsDeletedPermanentlySnackbar(itemsCount: number): void {
        const snackbarTextMessage =
            itemsCount +
            ' ' +
            this.appTextService.get(getObjectTypeNameTextId(this.objectType, itemsCount !== 1)) +
            ' ' +
            this.appTextService.get('history.deleted').toLowerCase();

        this.snackBar.showSnackbar({
            textMessage: snackbarTextMessage,
            buttonText: this.appTextService.get('common.dismiss'),
        });
    }

    protected getMediaBankImages(mediaBankFile: IMediaBankFile): IPreviewImageCellValue {
        return {
            image: this.getThumbnailImage(mediaBankFile),
            imageLarge: this.getImage(mediaBankFile, FiImageSize.Small),
            iconClass: this.mediaBankHelperService.getMediaBankFileIcon(mediaBankFile.FileType),
        };
    }

    private getThumbnailImage(mediaBankFile: IMediaBankFile): string {
        switch (mediaBankFile.FileType) {
            case FileType.Image:
            case FileType.Pdf:
                return this.mediaStorageService.getMediaFileUrl(mediaBankFile, {
                    size: this.isListLayout() ? FiImageSize.Thumbnail : FiImageSize.Small,
                });
        }
    }

    private getImage(mediaBankFile: IMediaBankFile, size: FiImageSize): string {
        if (mediaBankFile.FileType === FileType.Image)
            return this.mediaStorageService.getMediaFileUrl(mediaBankFile, { size: size });
        return this.getThumbnailImage(mediaBankFile);
    }

    public loadListDataFn(loadArgs: ILoadDataArgs) {
        const parameter = this.getItemsListParameter(loadArgs);
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };
        if (this.selectedTagId) {
            parameter.TagIds = [this.selectedTagId];
        }

        return this.fiMediaBankService.getMediaBankFilesListData(parameter);
    }

    protected getMediaBankSize(itemSize: number): string {
        return this.mediaBankHelperService.getFileSizeText(itemSize);
    }

    protected onTagClick(tagId: number): void {
        if (this.isTagSelected(tagId)) {
            return;
        }
        this.selectTagOption(tagId);
    }

    protected isSuperAdminColumnShown(): boolean {
        return this.filterType === SideListOptionType.All;
    }

    protected isNameColumnOffsetReset(): boolean {
        return this.hasCheckedColumn() || this.isFavoriteIconShown() || this.isSuperAdminColumnShown();
    }

    protected getMediaBankPublicationStatusClass(
        isItemNotLoaded: boolean,
        isPublished: boolean,
        isDeleted: boolean,
    ): string {
        if (isItemNotLoaded) return '';

        if (isDeleted) return 'fi-cell-red';

        if (isPublished) return 'fi-cell-green';

        return 'fi-cell-orange';
    }

    protected getMediaBankPublicationValue(isPublished: boolean, isDeleted: boolean): string {
        if (isDeleted) return this.appTextService.get('document.deleted');

        if (isPublished) return '';

        return this.appTextService.get('common.not_shared');
    }

    protected isMediaBankNotVisited(item: IMediaBankFile): boolean {
        return this.notificationHelper.isNotVisited(item);
    }
}
